import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadCloud, FileText, AlertTriangle, ExternalLink, Trash2, ShieldCheck, Loader2, Download, CheckCircle2, Plus, X } from "lucide-react";
import { formatDate, formatINR } from "@/lib/constants";

const POLICY_TYPES = ["Term Life", "Whole Life", "Endowment", "ULIP", "Health/Mediclaim", "Motor", "Other"];

/**
 * Insurance Policy Analyzer (Part 1).
 *
 * Two views: LIST (upload + prior analyses) and REVIEW (single-record editor
 * with the original document rendered side-by-side).
 */
export default function InsuranceAnalyzer({ clientId }) {
  const [list, setList] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = clientId ? { client_id: clientId } : {};
    const { data } = await api.get("/insurance-policies", { params });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const upload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (clientId) fd.append("client_id", clientId);
    try {
      const { data } = await api.post("/insurance-policies/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      if (data.processing_status === "Extraction Failed") {
        toast.error(`Extraction failed: ${data.extraction_error || "unknown"}`);
      } else {
        toast.success("Extracted — please review the draft");
      }
      setFile(null);
      setSelected(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete analysis of ${a.original_filename}?`)) return;
    try {
      await api.delete(`/insurance-policies/${a.id}`);
      toast.success("Deleted");
      if (selected?.id === a.id) setSelected(null);
      load();
    } catch { toast.error("Failed"); }
  };

  if (selected) {
    return <ReviewView analysis={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6">
      <Card className="border-border bg-white p-6" data-testid="policy-upload-card">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h3 className="font-dashboard text-base font-bold">Upload a policy</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          PDF or clear image (JPG/PNG) up to 20&nbsp;MB. The AI extracts the policy&apos;s details for review — nothing is applied until you save & verify.
        </p>
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <Input
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="policy-file-input"
          />
          <Button onClick={upload} disabled={!file || uploading} className="gap-2" data-testid="policy-analyze-btn">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</> : <><UploadCloud className="h-4 w-4" /> Analyse policy</>}
          </Button>
        </div>
        {file && <div className="mt-2 text-xs text-muted-foreground">Ready · {file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
      </Card>

      <div>
        <h4 className="mb-3 font-dashboard text-sm font-bold">Prior analyses</h4>
        {list.length === 0 && <p className="text-sm text-muted-foreground">No analyses yet. Upload a policy above.</p>}
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-md border border-border bg-white p-4" data-testid={`analysis-row-${a.id}`}>
              <button onClick={() => setSelected(a)} className="flex flex-1 items-center gap-3 text-left" data-testid={`analysis-open-${a.id}`}>
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">{a.original_filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.detected_policy_type || "—"}
                    {a.insurer_name && ` · ${a.insurer_name}`}
                    {a.policy_number && ` · #${a.policy_number}`}
                    <span className="mx-1">·</span>
                    {formatDate(a.uploaded_at)}
                  </div>
                </div>
              </button>
              <StatusChip status={a.processing_status} />
              <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`analysis-del-${a.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    "Processing": "bg-secondary text-muted-foreground",
    "Pending Review": "bg-accent/10 text-accent",
    "Reviewed & Saved": "bg-primary/10 text-primary",
    "Extraction Failed": "bg-destructive/10 text-destructive",
    "Uploaded": "bg-secondary text-muted-foreground",
  };
  return (
    <span className={`mr-3 rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[status] || "bg-secondary text-muted-foreground"}`}>
      {status}
    </span>
  );
}

function ReviewView({ analysis, onBack }) {
  const [rec, setRec] = useState(analysis);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const isImage = (rec.content_type || "").startsWith("image/");
  const hasClient = !!rec.client_id;
  const alreadyApplied = !!rec.applied_to_client_record;

  const patch = (k, v) => setRec({ ...rec, [k]: v });
  const patchTS = (k, v) => setRec({ ...rec, type_specific_fields: { ...(rec.type_specific_fields || {}), [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/insurance-policies/${rec.id}`, {
        detected_policy_type: rec.detected_policy_type,
        insurer_name: rec.insurer_name,
        policy_number: rec.policy_number,
        policy_holder_name: rec.policy_holder_name,
        sum_assured: rec.sum_assured != null ? Number(rec.sum_assured) : null,
        premium_amount: rec.premium_amount != null ? Number(rec.premium_amount) : null,
        premium_frequency: rec.premium_frequency,
        policy_term_years: rec.policy_term_years != null ? Number(rec.policy_term_years) : null,
        premium_paying_term_years: rec.premium_paying_term_years != null ? Number(rec.premium_paying_term_years) : null,
        policy_start_date: rec.policy_start_date,
        maturity_date: rec.maturity_date,
        nominee_name: rec.nominee_name,
        type_specific_fields: rec.type_specific_fields,
        brief_coverage_summary: rec.brief_coverage_summary,
      });
      toast.success("Review saved");
      onBack();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const saveAndApply = async () => {
    if (!hasClient) { toast.error("This analysis has no client attached"); return; }
    if (!window.confirm(alreadyApplied
      ? "Re-apply the reviewed details, overwriting the previously-applied version on this client?"
      : "Apply these reviewed details to the client's Insurance record?")) return;
    setApplying(true);
    try {
      // Save edits first so the applied row uses the reviewer's corrections
      await save._raw?.();
      await api.patch(`/insurance-policies/${rec.id}`, {
        detected_policy_type: rec.detected_policy_type,
        insurer_name: rec.insurer_name,
        policy_number: rec.policy_number,
        policy_holder_name: rec.policy_holder_name,
        sum_assured: rec.sum_assured != null ? Number(rec.sum_assured) : null,
        premium_amount: rec.premium_amount != null ? Number(rec.premium_amount) : null,
        premium_frequency: rec.premium_frequency,
        policy_term_years: rec.policy_term_years != null ? Number(rec.policy_term_years) : null,
        premium_paying_term_years: rec.premium_paying_term_years != null ? Number(rec.premium_paying_term_years) : null,
        policy_start_date: rec.policy_start_date,
        maturity_date: rec.maturity_date,
        nominee_name: rec.nominee_name,
        type_specific_fields: rec.type_specific_fields,
        brief_coverage_summary: rec.brief_coverage_summary,
      });
      const { data } = await api.post(`/insurance-policies/${rec.id}/save-apply`);
      if (data.coverage_gap_advice_id) {
        const m = data.coverage_gap_meta;
        toast.success(
          `Applied to client · Coverage-gap advice drafted (existing ₹${(m.existing_cover / 1e5).toFixed(1)}L vs ₹${(m.required_cover_10x / 1e5).toFixed(1)}L target)`,
          { duration: 8000 }
        );
      } else {
        toast.success(data.replaced ? "Re-applied to client" : "Applied to client");
      }
      onBack();
    } catch (err) { toast.error(err.response?.data?.detail || "Apply failed"); } finally { setApplying(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/insurance-policies/${rec.id}/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `policy-${rec.policy_number || rec.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || "PDF export failed"); }
  };

  const flaggedFields = new Set((rec.extraction_confidence_flags || []).map((f) => f.field_name));
  const isFlagged = (k) => flaggedFields.has(k);

  const t = rec.detected_policy_type;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground" data-testid="review-back-btn">← Back to list</button>
        <StatusChip status={rec.processing_status} />
      </div>

      {rec.processing_status === "Extraction Failed" && (
        <Card className="border-l-4 border-l-destructive bg-white p-4" data-testid="extraction-failed-msg">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Extraction failed</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{rec.extraction_error || "The AI could not read this document."}</p>
        </Card>
      )}

      {(rec.extraction_confidence_flags || []).length > 0 && (
        <Card className="border-l-4 border-l-accent bg-white p-4" data-testid="confidence-flags-card">
          <div className="mb-2 flex items-center gap-2 text-accent">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Fields to double-check ({rec.extraction_confidence_flags.length})</span>
          </div>
          <ul className="space-y-1 text-sm">
            {rec.extraction_confidence_flags.map((f, i) => (
              <li key={i} className="flex gap-2 text-muted-foreground">
                <span className="font-mono text-xs text-foreground">{f.field_name}</span>
                <span>—</span>
                <span>{f.reason}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        {/* LEFT: source document */}
        <Card className="border-border bg-white p-2 lg:sticky lg:top-6 lg:h-[calc(100vh-120px)] lg:self-start" data-testid="source-preview-card">
          <div className="mb-2 flex items-center justify-between px-2 pt-2">
            <span className="overline text-muted-foreground">Original document</span>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/insurance-policies/${rec.id}/download`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid="source-open-btn"
            >
              Open in tab <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="h-full overflow-hidden rounded-md border border-border bg-secondary/20">
            {isImage ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/insurance-policies/${rec.id}/download`}
                alt={rec.original_filename}
                className="h-full w-full object-contain"
              />
            ) : (
              <iframe
                src={`${process.env.REACT_APP_BACKEND_URL}/api/insurance-policies/${rec.id}/download`}
                title="Policy document"
                className="h-full w-full lg:min-h-[70vh]"
                style={{ minHeight: "60vh" }}
              />
            )}
          </div>
        </Card>

        {/* RIGHT: editable extraction */}
        <div className="space-y-4">
          {rec.brief_coverage_summary && (
            <Card className="border-border bg-white p-5" data-testid="brief-summary-card">
              <div className="mb-2 overline text-primary">Brief coverage report</div>
              <Textarea
                value={rec.brief_coverage_summary}
                onChange={(e) => patch("brief_coverage_summary", e.target.value)}
                rows={5}
                data-testid="brief-summary-input"
              />
            </Card>
          )}

          <Card className="border-border bg-white p-5">
            <SectionHeader>Basic details</SectionHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Policy type" flagged={isFlagged("detected_policy_type")}>
                <select
                  value={rec.detected_policy_type || ""}
                  onChange={(e) => patch("detected_policy_type", e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="edit-policy-type"
                >
                  {POLICY_TYPES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Insurer" flagged={isFlagged("insurer_name")}>
                <Input value={rec.insurer_name || ""} onChange={(e) => patch("insurer_name", e.target.value)} data-testid="edit-insurer" />
              </FieldRow>
              <FieldRow label="Policy number" flagged={isFlagged("policy_number")}>
                <Input value={rec.policy_number || ""} onChange={(e) => patch("policy_number", e.target.value)} data-testid="edit-policy-number" />
              </FieldRow>
              <FieldRow label="Policy holder" flagged={isFlagged("policy_holder_name")}>
                <Input value={rec.policy_holder_name || ""} onChange={(e) => patch("policy_holder_name", e.target.value)} data-testid="edit-holder" />
              </FieldRow>
              <FieldRow label="Start date" flagged={isFlagged("policy_start_date")}>
                <Input type="date" value={rec.policy_start_date?.slice(0, 10) || ""} onChange={(e) => patch("policy_start_date", e.target.value)} data-testid="edit-start-date" />
              </FieldRow>
              <FieldRow label="Maturity date" flagged={isFlagged("maturity_date")}>
                <Input type="date" value={rec.maturity_date?.slice(0, 10) || ""} onChange={(e) => patch("maturity_date", e.target.value)} data-testid="edit-maturity-date" />
              </FieldRow>
              <FieldRow label="Nominee" flagged={isFlagged("nominee_name")}>
                <Input value={rec.nominee_name || ""} onChange={(e) => patch("nominee_name", e.target.value)} data-testid="edit-nominee" />
              </FieldRow>
            </div>
          </Card>

          <Card className="border-border bg-white p-5">
            <SectionHeader>Coverage & premium</SectionHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Sum assured (₹)" flagged={isFlagged("sum_assured")} critical>
                <Input type="number" value={rec.sum_assured ?? ""} onChange={(e) => patch("sum_assured", e.target.value)} className="tabular-nums" data-testid="edit-sum-assured" />
              </FieldRow>
              <FieldRow label="Premium (₹)" flagged={isFlagged("premium_amount")} critical>
                <Input type="number" value={rec.premium_amount ?? ""} onChange={(e) => patch("premium_amount", e.target.value)} className="tabular-nums" data-testid="edit-premium" />
              </FieldRow>
              <FieldRow label="Premium frequency" flagged={isFlagged("premium_frequency")}>
                <select
                  value={rec.premium_frequency || ""}
                  onChange={(e) => patch("premium_frequency", e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="edit-frequency"
                >
                  <option value="">—</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half-yearly">Half-yearly</option>
                  <option value="annual">Annual</option>
                </select>
              </FieldRow>
              <FieldRow label="Policy term (yrs)" flagged={isFlagged("policy_term_years")}>
                <Input type="number" value={rec.policy_term_years ?? ""} onChange={(e) => patch("policy_term_years", e.target.value)} className="tabular-nums" data-testid="edit-policy-term" />
              </FieldRow>
              <FieldRow label="Premium paying term (yrs)" flagged={isFlagged("premium_paying_term_years")}>
                <Input type="number" value={rec.premium_paying_term_years ?? ""} onChange={(e) => patch("premium_paying_term_years", e.target.value)} className="tabular-nums" data-testid="edit-ppt" />
              </FieldRow>
            </div>
          </Card>

          <TypeSpecificSection type={t} rec={rec} patchTS={patchTS} isFlagged={isFlagged} />

          <div className="sticky bottom-2 z-10 flex flex-wrap justify-end gap-2 rounded-md border border-border bg-white/95 p-3 shadow-card backdrop-blur">
            <Button variant="outline" onClick={downloadPdf} className="gap-1" data-testid="review-pdf-btn">
              <Download className="h-4 w-4" /> PDF
            </Button>
            <Button variant="outline" onClick={onBack} data-testid="review-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={save} disabled={busy} data-testid="review-save-btn">
              {busy ? "Saving…" : "Save review"}
            </Button>
            {hasClient && (
              <Button
                onClick={saveAndApply}
                disabled={applying}
                className="gap-1"
                data-testid="review-save-apply-btn"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {applying ? "Applying…" : alreadyApplied ? "Re-apply to client" : "Save & Apply to client"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return <div className="mb-3 font-dashboard text-sm font-bold">{children}</div>;
}

function FieldRow({ label, flagged, critical, children }) {
  return (
    <div>
      <Label className={`text-xs ${flagged ? "text-accent" : critical ? "text-primary" : ""}`}>
        {label}
        {flagged && <span className="ml-1 rounded-full bg-accent/10 px-1.5 py-0.5 text-[9px] font-semibold text-accent">CHECK</span>}
        {critical && !flagged && <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold text-primary">CONFIRM</span>}
      </Label>
      {children}
    </div>
  );
}

function TypeSpecificSection({ type, rec, patchTS, isFlagged }) {
  const ts = rec.type_specific_fields || {};
  if (!type) return null;

  if (type === "Endowment" || type === "Whole Life" || type === "ULIP") {
    return (
      <Card className="border-border bg-white p-5" data-testid="section-savings">
        <SectionHeader>Savings & investment value</SectionHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow label="Current surrender value (₹)" critical flagged={isFlagged("surrender_value_current")}>
            <Input type="number" value={ts.surrender_value_current ?? ""} onChange={(e) => patchTS("surrender_value_current", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-surrender-value" />
          </FieldRow>
          <FieldRow label="Maturity benefit (₹)" flagged={isFlagged("maturity_benefit")}>
            <Input type="number" value={ts.maturity_benefit ?? ""} onChange={(e) => patchTS("maturity_benefit", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-maturity-benefit" />
          </FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Bonus details" flagged={isFlagged("bonus_details")}>
              <Input value={ts.bonus_details || ""} onChange={(e) => patchTS("bonus_details", e.target.value)} data-testid="edit-bonus" />
            </FieldRow>
          </div>
          {type === "ULIP" && (
            <div className="md:col-span-2">
              <FieldRow label="Fund options">
                <Input value={(ts.fund_options || []).join(", ")} onChange={(e) => patchTS("fund_options", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} data-testid="edit-fund-options" placeholder="Equity Growth, Balanced, Debt…" />
              </FieldRow>
            </div>
          )}
        </div>
        {(ts.surrender_value_schedule || []).length > 0 || true ? (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="overline text-muted-foreground">Surrender value schedule</span>
              <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => patchTS("surrender_value_schedule", [...(ts.surrender_value_schedule || []), { year: "", value: "" }])} data-testid="add-surrender-row-btn">
                <Plus className="h-3 w-3" /> Add year
              </Button>
            </div>
            {(ts.surrender_value_schedule || []).length === 0 && <p className="text-xs text-muted-foreground">No schedule extracted. Add rows if applicable.</p>}
            {(ts.surrender_value_schedule || []).length > 0 && (
              <div className="space-y-1">
                {(ts.surrender_value_schedule || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[80px_1fr_auto] gap-2 rounded-md border border-border bg-secondary/20 p-2" data-testid={`surrender-row-${i}`}>
                    <Input type="number" placeholder="Year" value={r.year ?? ""} onChange={(e) => {
                      const next = [...ts.surrender_value_schedule];
                      next[i] = { ...next[i], year: Number(e.target.value) || null };
                      patchTS("surrender_value_schedule", next);
                    }} className="h-8 tabular-nums text-xs" />
                    <Input type="number" placeholder="Value (₹)" value={r.value ?? ""} onChange={(e) => {
                      const next = [...ts.surrender_value_schedule];
                      next[i] = { ...next[i], value: Number(e.target.value) || null };
                      patchTS("surrender_value_schedule", next);
                    }} className="h-8 tabular-nums text-xs" />
                    <Button size="sm" variant="ghost" className="h-8 w-8" onClick={() => {
                      const next = ts.surrender_value_schedule.filter((_, j) => j !== i);
                      patchTS("surrender_value_schedule", next);
                    }} data-testid={`rm-surrender-${i}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </Card>
    );
  }

  if (type === "Term Life") {
    return (
      <Card className="border-border bg-white p-5" data-testid="section-term">
        <SectionHeader>Term-specific</SectionHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow label="Critical illness cover (₹)" flagged={isFlagged("critical_illness_cover")}>
            <Input type="number" value={ts.critical_illness_cover ?? ""} onChange={(e) => patchTS("critical_illness_cover", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-ci-cover" />
          </FieldRow>
          <FieldRow label="Accidental death benefit (₹)" flagged={isFlagged("accidental_death_benefit")}>
            <Input type="number" value={ts.accidental_death_benefit ?? ""} onChange={(e) => patchTS("accidental_death_benefit", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-adb" />
          </FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Waiting period" flagged={isFlagged("waiting_period")}>
              <Input value={ts.waiting_period || ""} onChange={(e) => patchTS("waiting_period", e.target.value)} data-testid="edit-waiting-period" />
            </FieldRow>
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="overline text-muted-foreground">Riders</span>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => patchTS("riders", [...(ts.riders || []), { name: "", cover_amount: "" }])} data-testid="add-rider-btn">
              <Plus className="h-3 w-3" /> Add rider
            </Button>
          </div>
          {(ts.riders || []).length === 0 && <p className="text-xs text-muted-foreground">No riders extracted.</p>}
          {(ts.riders || []).map((r, i) => (
            <div key={i} className="mb-1 grid grid-cols-[1fr_140px_auto] gap-2 rounded-md border border-border bg-secondary/20 p-2" data-testid={`rider-row-${i}`}>
              <Input placeholder="Rider name" value={r.name || ""} onChange={(e) => {
                const next = [...ts.riders]; next[i] = { ...next[i], name: e.target.value }; patchTS("riders", next);
              }} className="h-8 text-xs" />
              <Input type="number" placeholder="Cover (₹)" value={r.cover_amount ?? ""} onChange={(e) => {
                const next = [...ts.riders]; next[i] = { ...next[i], cover_amount: Number(e.target.value) || null }; patchTS("riders", next);
              }} className="h-8 tabular-nums text-xs" />
              <Button size="sm" variant="ghost" className="h-8 w-8" onClick={() => patchTS("riders", ts.riders.filter((_, j) => j !== i))} data-testid={`rm-rider-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (type === "Health/Mediclaim") {
    return (
      <Card className="border-border bg-white p-5" data-testid="section-health">
        <SectionHeader>Health / Mediclaim</SectionHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow label="Sum insured per member (₹)" critical flagged={isFlagged("sum_insured_per_member")}>
            <Input type="number" value={ts.sum_insured_per_member ?? ""} onChange={(e) => patchTS("sum_insured_per_member", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-si-member" />
          </FieldRow>
          <FieldRow label="Room rent limit" flagged={isFlagged("room_rent_limit")}>
            <Input value={ts.room_rent_limit || ""} onChange={(e) => patchTS("room_rent_limit", e.target.value)} data-testid="edit-room-rent" />
          </FieldRow>
          <FieldRow label="Co-pay %" flagged={isFlagged("co_pay_pct")}>
            <Input type="number" value={ts.co_pay_pct ?? ""} onChange={(e) => patchTS("co_pay_pct", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-copay" />
          </FieldRow>
          <FieldRow label="No-claim bonus %" flagged={isFlagged("no_claim_bonus_pct")}>
            <Input type="number" value={ts.no_claim_bonus_pct ?? ""} onChange={(e) => patchTS("no_claim_bonus_pct", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-ncb" />
          </FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Waiting period · pre-existing conditions" flagged={isFlagged("waiting_period_pre_existing_conditions")}>
              <Input value={ts.waiting_period_pre_existing_conditions || ""} onChange={(e) => patchTS("waiting_period_pre_existing_conditions", e.target.value)} data-testid="edit-wp-pec" />
            </FieldRow>
          </div>
          <div className="md:col-span-2">
            <FieldRow label="Restoration benefit" flagged={isFlagged("restoration_benefit")}>
              <Input value={ts.restoration_benefit || ""} onChange={(e) => patchTS("restoration_benefit", e.target.value)} data-testid="edit-restoration" />
            </FieldRow>
          </div>
          <div className="md:col-span-2">
            <FieldRow label="Network hospitals note">
              <Textarea rows={2} value={ts.network_hospitals_note || ""} onChange={(e) => patchTS("network_hospitals_note", e.target.value)} data-testid="edit-network-note" />
            </FieldRow>
          </div>
        </div>
      </Card>
    );
  }

  if (type === "Motor") {
    return (
      <Card className="border-border bg-white p-5" data-testid="section-motor">
        <SectionHeader>Motor</SectionHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <FieldRow label="IDV (₹)" critical flagged={isFlagged("idv")}>
            <Input type="number" value={ts.idv ?? ""} onChange={(e) => patchTS("idv", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-idv" />
          </FieldRow>
          <FieldRow label="NCB %" flagged={isFlagged("ncb_pct")}>
            <Input type="number" value={ts.ncb_pct ?? ""} onChange={(e) => patchTS("ncb_pct", Number(e.target.value) || null)} className="tabular-nums" data-testid="edit-ncb-motor" />
          </FieldRow>
        </div>
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="overline text-muted-foreground">Add-ons</span>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => patchTS("add_ons", [...(ts.add_ons || []), { name: "", cover_details: "" }])} data-testid="add-addon-btn">
              <Plus className="h-3 w-3" /> Add add-on
            </Button>
          </div>
          {(ts.add_ons || []).length === 0 && <p className="text-xs text-muted-foreground">No add-ons extracted.</p>}
          {(ts.add_ons || []).map((r, i) => (
            <div key={i} className="mb-1 grid grid-cols-[180px_1fr_auto] gap-2 rounded-md border border-border bg-secondary/20 p-2" data-testid={`addon-row-${i}`}>
              <Input placeholder="Add-on name" value={r.name || ""} onChange={(e) => {
                const next = [...ts.add_ons]; next[i] = { ...next[i], name: e.target.value }; patchTS("add_ons", next);
              }} className="h-8 text-xs" />
              <Input placeholder="Cover details" value={r.cover_details || ""} onChange={(e) => {
                const next = [...ts.add_ons]; next[i] = { ...next[i], cover_details: e.target.value }; patchTS("add_ons", next);
              }} className="h-8 text-xs" />
              <Button size="sm" variant="ghost" className="h-8 w-8" onClick={() => patchTS("add_ons", ts.add_ons.filter((_, j) => j !== i))} data-testid={`rm-addon-${i}`}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return null;
}
